import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NJButton, NJText } from '@engie-group/fluid-design-system-react';
import { UMFormStyles } from '../../urbanmap/_components';
import { CreationStepper, perimeterProgress, type ProgressItem, type SectionKey } from './CreationProgress';
import { HomeRail } from './HomeChrome';
import './home.css';
import { calibrateRatios, centerOf, cloneRatios, countModifiedRatios, defaultContract, inPerimeter, missingRatioCount, nearestStation, ratiosForDegrees, stationOf, REF_DJCLIM, REF_DJU } from './domain';
import { territoryClimate, territoryRatioDefaults } from './territoryClimate';
import { useCreateProject } from './hooks';
import { FRANCE_OVERVIEW_BOUNDS } from './PerimeterMap';
import { StepPerimeter, type PerimeterDraft } from './StepPerimeter';
import { StepRecap, type CopyStatus } from './StepRecap';
import { StepSettings } from './StepSettings';
import { UMR } from './tokens';
import type { DegreeDays, DjuSettings, Perimeter, PlanProject, RatioTable as Ratios, Referential } from './types';

interface Props {
  referential: Referential;
  onCancel: () => void;
  onOpen: (projectId: string) => void;
  flash: (message: string) => void;
  source?: PlanProject;
}

/** Assistant de création, plein écran : périmètre → météo et ratios → récapitulatif. */
export function CreateWizard({ referential, onCancel, onOpen, flash, source }: Props) {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<PerimeterDraft>({
    name: source ? `${source.name} · copie` : '', description: source?.description ?? '',
    kind: source?.kind ?? 'new', networkIds: source ? [...source.networkIds] : [], contract: source ? { ...source.contract } : defaultContract(),
    mode: source?.perimeter.kind === 'drawn' ? 'drawn' : 'communes',
    communes: source?.perimeter.kind === 'communes' ? [...source.perimeter.communes] : [],
    shape: source?.perimeter.kind === 'drawn' ? 'free' : 'rectangle',
    points: source?.perimeter.kind === 'drawn' ? source.perimeter.polygon.map(([lng, lat]) => [lng, lat]) : [],
    closed: source?.perimeter.kind === 'drawn', drawing: false,
  });
  const [stationId, setStationId] = useState(() => stationOf(source?.dju ?? referential.defaultDju).id);
  const [stationPicked, setStationPicked] = useState(!!source);
  const station = useMemo(() => stationOf({ stationId, reference: 0 }), [stationId]);
  /** Climat cible, vérifié avant le calcul des ratios. */
  const [stationDegrees, setStationDegrees] = useState<DegreeDays>(() => {
    const s = stationOf(source?.dju ?? referential.defaultDju);
    return { dju: source?.dju.reference ?? s.dju, djclim: source?.dju.djclim ?? s.djclim };
  });
  const chooseStation = (id: string) => {
    const s = referential.stations.find((x) => x.id === id);
    if (!s) return;
    setStationId(id);
    setStationPicked(true);
    setStationDegrees({ dju: s.dju, djclim: s.djclim });
  };

  /** Degrés-jours auxquels le référentiel de ratios est établi. */
  const legacyDegrees = useMemo<DegreeDays>(() => ({ dju: REF_DJU, djclim: REF_DJCLIM }), []);
  const [myDegrees, setMyDegrees] = useState<DegreeDays>(source ? stationDegrees : legacyDegrees);
  /** Préserver les valeurs et coefficients de chaque poste, y compris les anciens projets. */
  const [myRatios, setMyRatios] = useState<Ratios>(() => cloneRatios(source?.ratios ?? referential.defaultRatios));
  const automatic = useRef({ ratios: cloneRatios(referential.defaultRatios), degrees: legacyDegrees });
  const [initializedFor, setInitializedFor] = useState<string | null>(source ? JSON.stringify(source.perimeter) : null);
  const [needsInitializationDecision, setNeedsInitializationDecision] = useState(false);
  /** Entrées du dernier calcul validé, conservées jusqu'au prochain calcul explicite. */
  const [applied, setApplied] = useState<{ degrees: DegreeDays; ratios: Ratios; stationDegrees: DegreeDays; stationId: string; perimeterKey: string }>(
    () => ({ degrees: myDegrees, ratios: cloneRatios(myRatios), stationDegrees, stationId, perimeterKey: source ? JSON.stringify(source.perimeter) : '' }),
  );
  /** Mes ratios n'alimentent le projet qu'après un premier calcul explicite. */
  const [appliedOnce, setAppliedOnce] = useState(!!source);

  const dju = useMemo<DjuSettings>(() => ({ stationId, reference: stationDegrees.dju, djclim: stationDegrees.djclim }), [stationId, stationDegrees]);
  /** Référentiel calibré sur les degrés-jours retenus : sert de base de comparaison. */
  const stationRatios = useMemo(() => ratiosForDegrees(stationDegrees.dju, stationDegrees.djclim), [stationDegrees]);
  /** Ratios réellement utilisés : mes ratios appliqués, calibrés sur les DJ de la station. */
  const ratios = useMemo(
    () => source && applied.degrees.dju === applied.stationDegrees.dju && applied.degrees.djclim === applied.stationDegrees.djclim
      ? cloneRatios(applied.ratios) : calibrateRatios(applied.ratios, applied.degrees, applied.stationDegrees),
    [applied, source],
  );

  const [ratiosValid, setRatiosValid] = useState(true);
  const [ratiosEditing, setRatiosEditing] = useState(false);
  const [settingsVisited, setSettingsVisited] = useState(false);
  const [touched, setTouched] = useState<Partial<Record<SectionKey, boolean>>>({});
  const [status, setStatus] = useState<CopyStatus>('idle');
  const [progress, setProgress] = useState(0);
  const create = useCreateProject();

  const resetCopy = () => { setStatus('idle'); setProgress(0); create.reset(); };
  const patch = useCallback((p: Partial<PerimeterDraft>) => {
    setDraft((d) => ({ ...d, ...p }));
    setStatus('idle');
    setProgress(0);
    if (p.contract) setTouched((t) => ({ ...t, contract: true }));
  }, []);
  const perimeter = useMemo<Perimeter>(
    () => (draft.mode === 'communes' ? { kind: 'communes', communes: draft.communes } : { kind: 'drawn', polygon: draft.closed ? draft.points : [] }),
    [draft.mode, draft.communes, draft.points, draft.closed],
  );
  const included = useMemo(() => referential.buildings.filter((b) => inPerimeter(b, perimeter)), [referential.buildings, perimeter]);
  const includedIds = useMemo(() => new Set(included.map((b) => b.id)), [included]);
  const climate = useMemo(() => territoryClimate(perimeter, included, referential), [perimeter, included, referential]);
  const defaults = useMemo(() => territoryRatioDefaults(climate.zone), [climate.zone]);
  const baseRatios = defaults.ratios;
  const defaultDegrees = defaults.degrees;
  const perimeterKey = JSON.stringify(perimeter);
  const pending = !appliedOnce || applied.perimeterKey !== perimeterKey
    || countModifiedRatios(applied.ratios, myRatios) > 0
    || applied.degrees.dju !== myDegrees.dju || applied.degrees.djclim !== myDegrees.djclim
    || applied.stationDegrees.dju !== stationDegrees.dju || applied.stationDegrees.djclim !== stationDegrees.djclim
    || applied.stationId !== stationId;
  const custom = countModifiedRatios(applied.ratios, baseRatios) > 0
    || applied.degrees.dju !== defaultDegrees.dju || applied.degrees.djclim !== defaultDegrees.djclim;
  const initialize = () => {
    setMyRatios(cloneRatios(baseRatios));
    setMyDegrees({ ...defaultDegrees });
    automatic.current = { ratios: cloneRatios(baseRatios), degrees: { ...defaultDegrees } };
    setNeedsInitializationDecision(false);
    setInitializedFor(perimeterKey);
    resetCopy();
  };
  const enterRatios = () => {
    if (initializedFor !== perimeterKey) {
      const personalized = !!source || countModifiedRatios(myRatios, automatic.current.ratios) > 0
        || myDegrees.dju !== automatic.current.degrees.dju || myDegrees.djclim !== automatic.current.degrees.djclim;
      if (personalized) {
        setNeedsInitializationDecision(true);
        setInitializedFor(perimeterKey);
      } else initialize();
    }
    setSettingsVisited(true); setStep(1);
  };
  /** Tant que l'utilisateur n'a pas choisi, la station la plus proche du périmètre est proposée. */
  const center = useMemo(() => centerOf(included.map((b) => b.centroid)), [included]);
  useEffect(() => {
    if (stationPicked || !center) return;
    const near = nearestStation(center, referential.stations);
    setStationId(near.id);
    setStationDegrees({ dju: near.dju, djclim: near.djclim });
  }, [center, stationPicked, referential.stations]);
  const perimeterLabel = perimeter.kind === 'drawn' ? 'Zone dessinée à la main' : draft.communes.map((c) => referential.communes.find((x) => x.insee === c)?.name ?? c).join(', ');
  const networkNames = draft.networkIds.map(id => {
    const network = referential.networks.find(item => item.id === id);
    const name = network?.name ?? `Réseau sans nom (${id})`;
    return network?.code ? `${network.code} — ${name}` : name;
  });

  useEffect(() => {
    if (status !== 'copying') return undefined;
    const t = window.setInterval(() => setProgress((p) => Math.min(create.isSuccess ? 100 : 90, p + 8)), 180);
    return () => window.clearInterval(t);
  }, [status, create.isSuccess]);
  useEffect(() => { if (status === 'copying' && progress >= 100) setStatus('done'); }, [status, progress]);

  const launch = () => {
    if (!step0Valid || !step1Valid || status === 'copying') return;
    setStatus('copying');
    setProgress(0);
    create.mutate(
      {
        name: draft.name.trim(), description: draft.description.trim() || undefined,
        sourceProjectId: source?.id,
        kind: draft.kind, networkIds: draft.networkIds, contract: draft.contract,
        perimeter, ratios, dju, usesDefaults: !custom,
      },
      { onError: () => setStatus('error') },
    );
  };

  const sections = perimeterProgress(draft, included.length, referential.stations.some((s) => s.id === stationId), touched);
  const sectionItems = Object.values(sections);
  const step0Valid = sectionItems.every((item) => item.state === 'success');
  const step1Valid = !!climate.zone && !needsInitializationDecision && ratiosValid && !ratiosEditing && appliedOnce && !pending;
  const nextFromRatios = () => { if (step0Valid && step1Valid) setStep(2); };
  const step0Progress: ProgressItem = {
    state: step0Valid ? 'success' : sectionItems.some((item) => item.state === 'error') ? 'error' : step === 0 ? 'current' : 'incomplete',
    detail: `${sectionItems.filter((item) => item.state === 'success').length} sur 5 rubriques validées`,
  };
  const step1Progress: ProgressItem = {
    state: !ratiosValid ? 'error' : ratiosEditing || (pending && appliedOnce) || (step1Valid && missingRatioCount(ratios) > 0) ? 'incomplete' : step1Valid ? 'success' : step === 1 ? 'current' : settingsVisited ? 'incomplete' : 'untouched',
    detail: !climate.zone ? 'Source climatique indisponible pour ce périmètre' : needsInitializationDecision ? 'Valeurs personnalisées à confirmer'
      : !ratiosValid ? 'Valeurs hors plage' : ratiosEditing ? 'Édition à terminer' : step1Valid
        ? missingRatioCount(ratios) > 0 ? 'Calcul incomplet : ratios manquants' : 'Ratios calculés pour le projet'
        : pending && appliedOnce ? 'Recalcul des ratios requis' : 'Calcul des ratios requis',
  };
  const step2Progress: ProgressItem = {
    state: status === 'done' ? 'success' : status === 'error' ? 'error' : status === 'copying' ? 'busy' : step === 2 ? 'current' : 'untouched',
    detail: status === 'done' ? 'Projet créé' : status === 'error' ? 'Échec de la copie · réessayez' : status === 'copying' ? `${progress} %` : step === 2 ? 'Prêt à créer le projet' : 'Vérification avant copie',
  };
  const busy = status === 'copying';
  const actions = (
    <div className="pcv3-creation-actions" aria-label="Actions de création" role="group">
      {status === 'done' ? (
        <NJButton variant="primary" scale="md" onClick={() => create.data && onOpen(create.data.id)} label="Ouvrir le projet" />
      ) : (
        <>
          <NJButton variant="secondary" scale="md" disabled={busy} onClick={onCancel} label="Annuler" />
          {step > 0 && <NJButton variant="secondary" scale="md" disabled={busy} onClick={() => setStep((s) => s - 1)} label="Précédent" />}
          {step === 0 && <NJButton variant="primary" scale="md" disabled={!step0Valid} onClick={enterRatios} label="Suivant" />}
          {step === 1 && <NJButton variant="primary" scale="md" disabled={!step0Valid || !step1Valid} onClick={nextFromRatios} label="Suivant" />}
          {step === 2 && <NJButton variant="primary" scale="md" disabled={busy || !step0Valid || !step1Valid} onClick={launch} label={status === 'error' ? 'Réessayer' : 'Créer le projet'} />}
        </>
      )}
    </div>
  );

  return (
    <div data-comment-view={`creation-step-${step}`} style={{ position: 'absolute', inset: 0, zIndex: 40, display: 'flex', background: UMR.white, fontFamily: UMR.font, color: UMR.black }}>
      <HomeRail onHome={onCancel} flash={flash} isHome={false} disabled={busy} />
      <div className="pcv3-home-content">
      <UMFormStyles />
      <CreationStepper current={step} items={[step0Progress, step1Progress, step2Progress]} />
      {source && <NJText className="pcv3-duplicate-notice" role="status">
        Copie de « {source.name} » : ratios, hypothèses climatiques et période du contrat conservés.
        Vous pouvez changer le périmètre de cette nouvelle variante. Le projet source reste inchangé.
      </NJText>}

      <div style={{ flex: 1, minHeight: 0 }}>
        <div hidden={step !== 0} style={{ height: '100%' }}>
          <StepPerimeter
            referential={referential}
            draft={draft}
            onChange={patch}
            included={included}
            includedIds={includedIds}
            stationId={stationId}
            onStationSelect={(id) => { resetCopy(); chooseStation(id); }}
            progress={sections}
            onTouch={(key) => setTouched((t) => t[key] ? t : { ...t, [key]: true })}
            actions={step === 0 ? actions : null}
            initialBounds={FRANCE_OVERVIEW_BOUNDS}
          />
        </div>
        {settingsVisited && (
          <div hidden={step !== 1} style={{ height: '100%' }}>
          <StepSettings
            provenance={climate.explanation}
            initializationBlocked={!climate.zone || needsInitializationDecision}
            initialization={needsInitializationDecision && <div role="alert">
              <NJText>Le périmètre a changé. Vos ratios personnalisés ou dupliqués sont conservés sans écrasement.
                {climate.zone ? ' Confirmez leur conservation ou réinitialisez-les depuis la source du nouveau périmètre.' : ' Aucun jeu climatique unique ne peut être initialisé pour ce périmètre.'}</NJText>
              {climate.zone && <div className="pcv3-climate-source">
                <NJButton variant="secondary" onClick={() => { setNeedsInitializationDecision(false); resetCopy(); }} label="Conserver mes ratios personnalisés" />
                <NJButton variant="secondary" onClick={initialize} label="Réinitialiser depuis le périmètre" />
              </div>}
            </div>}
            referential={referential}
            defaultRatios={baseRatios}
            station={station}
            stationDegrees={stationDegrees}
            onStationDegreesChange={(next) => { resetCopy(); setStationDegrees(next); }}
            myDegrees={myDegrees}
            myRatios={myRatios}
            appliedDegrees={applied.degrees}
            resultDegrees={applied.stationDegrees}
            resultStation={stationOf({ stationId: applied.stationId, reference: 0 })}
            defaultDegrees={defaultDegrees}
            onMyDegreesChange={(next) => { resetCopy(); setMyDegrees(next); }}
            onMyRatiosChange={(next) => { resetCopy(); setMyRatios(next); }}
            projectRatios={ratios}
            stationRatios={stationRatios}
            pending={pending}
            applied={appliedOnce}
            onApply={() => { resetCopy(); setApplied({ degrees: myDegrees, ratios: myRatios, stationDegrees, stationId, perimeterKey }); setAppliedOnce(true); }}
            onRatiosValidity={setRatiosValid}
            onEditingChange={(editing) => { resetCopy(); setRatiosEditing(editing); }}
            actions={step === 1 ? actions : null}
            onNext={nextFromRatios}
            nextDisabled={!step0Valid || !step1Valid}
          />
          </div>
        )}
        {step === 2 && (
          <StepRecap
            name={draft.name.trim()}
            description={draft.description.trim()}
            kind={draft.kind}
            networkNames={networkNames}
            contract={draft.contract}
            perimeterLabel={perimeterLabel}
            buildingCount={included.length}
            incompleteRatios={missingRatioCount(ratios)}
            stationName={stationOf({ stationId: applied.stationId, reference: 0 }).name}
            dju={applied.stationDegrees.dju}
            djclim={applied.stationDegrees.djclim}
            referenceDegrees={applied.degrees}
            referenceZone={applied.ratios.climateZone}
            projectZone={climate.zone}
            status={status}
            progress={progress}
            actions={actions}
          />
        )}
      </div>

      </div>
    </div>
  );
}
